import { extractTranslations } from '@assets/i18n/config';
import type { UserNotificationPreference } from '@interfaces/organizationUser';

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';

import { UserContext } from '@providers/userContextProvider';
import { Box, IconButton, Typography, useTheme } from 'hephaestus/web';
import { Close as CloseIcon } from 'hephaestus/webIcons';
import { omit } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationPreferencesSettings } from './notificationPreferencesSettings';

export const NotificationPreferencesModal = ({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const { t } = useTranslation('components');
  const theme = useTheme();
  const { userContext, refetch } = useContext(UserContext);
  const [notificationPreferences, setNotificationPreferences] = useState<
    UserNotificationPreference[]
  >([]);

  useEffect(
    () =>
      setNotificationPreferences([
        ...(userContext.notificationPreferences ?? [])
          .filter((e) =>
            (e.notificationGroup?.notificationChannels ?? []).some(
              (el) => el.value,
            ),
          )
          .map((elem) => ({
            notificationGroup: {
              id: elem.notificationGroup.id,
              name: extractTranslations(elem.notificationGroup.name),
              description: elem.notificationGroup.description
                ? extractTranslations(elem.notificationGroup.description)
                : {},
              notificationChannels: (
                elem.notificationGroup?.notificationChannels ?? []
              ).map((nc) => omit(nc, ['__typename'])),
            },
            preferences: omit(elem.preferences, ['__typename']),
          })),
      ] as UserNotificationPreference[]),
    [userContext.notificationPreferences],
  );

  const handleClose = () => {
    refetch();
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      sx={{
        '.MuiDialog-paper': {
          minHeight: 400,
          borderRadius: theme.borderRadius.xxl,
        },
      }}
      fullWidth
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingY: 2.5,
          borderBottom: `1px ${theme.color.primaryContainer} solid`,
        }}
      >
        <Typography variant='headingLarge'>
          {t('notificationSettings.title')}
        </Typography>
        <IconButton
          aria-label='close'
          size='xsmall'
          onClick={() => setOpen(false)}
        >
          <CloseIcon sx={{ color: theme.color.onSurface }} fontSize='inherit' />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ paddingX: 2.5 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            mb: 1,
          }}
        >
          {notificationPreferences.map((elem) => (
            <NotificationPreferencesSettings
              key={elem.notificationGroup.id}
              notificationPreference={elem}
              notificationPreferences={notificationPreferences}
              setNotificationPreferences={setNotificationPreferences}
            />
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
